const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

export const isHtmlContent = (content = '') => /<\/?[a-z][\s\S]*>/i.test(String(content || ''));

export const plainTextToHtml = (content = '') => String(content || '')
  .split(/\n{2,}/)
  .map((block) => {
    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);

    if (!lines.length) {
      return '';
    }

    if (lines.length === 1 && lines[0].length <= 56 && !/^\d+\./.test(lines[0])) {
      return `<h2>${escapeHtml(lines[0])}</h2>`;
    }

    return `<p>${lines.map(escapeHtml).join('<br>')}</p>`;
  })
  .filter(Boolean)
  .join('');

export const normalizeContentToHtml = (content = '') => (
  isHtmlContent(content) ? String(content || '') : plainTextToHtml(content)
);

export const htmlToText = (html = '') => String(html || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/(p|h1|h2|h3|li|div)>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .trim();

const normalizeHeadingSlug = (value = '') => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 64)
  || 'section';

const makeUniqueHeadingId = (title, usedIds) => {
  const baseId = `wiki-heading-${normalizeHeadingSlug(title)}`;
  let id = baseId;
  let index = 2;

  while (usedIds.has(id)) {
    id = `${baseId}-${index}`;
    index += 1;
  }

  usedIds.add(id);
  return id;
};

const getTiptapNodeText = (node = {}) => {
  if (node.text) {
    return node.text;
  }

  if (!Array.isArray(node.content)) {
    return '';
  }

  return node.content.map(getTiptapNodeText).join('');
};

export const buildTocEntriesFromTiptapDocument = (document = {}) => {
  const entries = [];
  const usedIds = new Set();

  const walk = (node) => {
    if (!node || typeof node !== 'object') {
      return;
    }

    if (node.type === 'heading') {
      const title = getTiptapNodeText(node).trim();
      const level = Number(node.attrs?.level || 2);
      if (title && level >= 1 && level <= 3) {
        entries.push({
          id: makeUniqueHeadingId(title, usedIds),
          level,
          title,
        });
      }
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };

  walk(document);
  return entries.slice(0, 12);
};

export const buildRichToc = (content = '') => {
  if (!isHtmlContent(content)) {
    const usedIds = new Set();
    return String(content || '')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !/^\d+\./.test(line) && line.length <= 54)
      .slice(0, 6)
      .map((title) => ({
        id: makeUniqueHeadingId(title, usedIds),
        level: 2,
        title,
      }));
  }

  if (typeof window === 'undefined') {
    return [];
  }

  const template = document.createElement('template');
  template.innerHTML = content;
  const usedIds = new Set();
  return [...template.content.querySelectorAll('h1, h2, h3')]
    .map((heading) => {
      const title = heading.textContent.trim();
      return {
        id: makeUniqueHeadingId(title, usedIds),
        level: Number(heading.tagName.slice(1)),
        title,
      };
    })
    .filter((entry) => entry.title)
    .filter(Boolean)
    .slice(0, 8);
};

export const sanitizeWikiHtml = (html = '') => {
  if (typeof window === 'undefined') {
    return html;
  }

  const allowedTags = new Set([
    'A',
    'B',
    'BLOCKQUOTE',
    'BR',
    'DIV',
    'EM',
    'H1',
    'H2',
    'H3',
    'I',
    'IMG',
    'LI',
    'OL',
    'P',
    'PRE',
    'CODE',
    'STRONG',
    'TABLE',
    'TBODY',
    'TD',
    'TH',
    'THEAD',
    'TR',
    'UL',
  ]);
  const allowedAttributes = {
    A: ['href', 'target', 'rel'],
    DIV: ['class', 'style'],
    H1: ['style'],
    H2: ['style'],
    H3: ['style'],
    IMG: ['src', 'alt', 'title', 'width', 'height'],
    P: ['style'],
    TABLE: ['class'],
    TD: ['style'],
    TH: ['style'],
  };
  const template = document.createElement('template');
  template.innerHTML = normalizeContentToHtml(html);

  const cleanNode = (node) => {
    [...node.children].forEach((child) => {
      if (!allowedTags.has(child.tagName)) {
        child.replaceWith(document.createTextNode(child.textContent || ''));
        return;
      }

      [...child.attributes].forEach((attribute) => {
        const allowed = allowedAttributes[child.tagName] || [];
        if (!allowed.includes(attribute.name)) {
          child.removeAttribute(attribute.name);
        }
      });

      if (child.tagName === 'A') {
        const href = child.getAttribute('href') || '';
        if (!/^https?:\/\//i.test(href) && !href.startsWith('#')) {
          child.removeAttribute('href');
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noreferrer');
      }

      if (child.tagName === 'IMG') {
        const src = child.getAttribute('src') || '';
        if (!/^https?:\/\//i.test(src) && !src.startsWith('/') && !/^data:image\/(png|jpe?g|gif|webp);base64,/i.test(src)) {
          child.removeAttribute('src');
        }
      }

      if (child.hasAttribute('style')) {
        const textAlign = child.style.textAlign;
        if (['left', 'center', 'right', 'justify'].includes(textAlign)) {
          child.setAttribute('style', `text-align: ${textAlign}`);
        } else {
          child.removeAttribute('style');
        }
      }

      cleanNode(child);
    });
  };

  cleanNode(template.content);
  return template.innerHTML;
};
